import React from 'react';
import { Field, ErrorMessage, FormikErrors, FormikTouched } from 'formik';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import { SellerInformationContext } from '@context/seller-information.context';
import { useContextSelector } from 'use-context-selector';
import useWindowSize from '@hooks/use-window-size';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { SellerFormValueType as FormValueType } from '@interfaces/models/seller-info/seller-information';
import styles from '../seller-information-form/seller-information-form.module.scss';
import TaxInfoModal from '../tax-info-modal/tax-info-modal';
import SellerFormTextInput from './seller-form-text-input';

type TaxInformationProps = {
  values: FormValueType;
  errors?: FormikErrors<FormValueType>;
  touched?: FormikTouched<FormValueType>;
  dirty?: boolean;
  ErrorLabel: (message: string) => React.ReactElement;
};

const TaxInformation: React.FC<TaxInformationProps> = (props): React.ReactElement => {
  const { values, errors, touched, ErrorLabel, dirty } = props;
  const countries = useContextSelector(SellerInformationContext, (value) => value.countries);
  const userFlags = useContextSelector(SellerInformationContext, (value) => value.userFlags);
  const taxInfo = useContextSelector(SellerInformationContext, (value) => value.taxInfo);

  const { t } = useTranslation('get-paid');
  const { t: tA11y } = useTranslation('a11y');
  const { sendAnalyticEvent: sendSellerInfoAnalyticEvents } = useAnalyticEvents('seller_info');
  const { isWiderThanMd } = useWindowSize();

  const handleInfoIconClick = (): void => {
    sendSellerInfoAnalyticEvents('seller_tax_info_tin');
  };

  return (
    <div className={clsx(styles.formGroup, styles['formGroup--formGroupFlexContainer'])}>
      <div
        className={clsx(
          styles.formTitle,
          styles['formTitle--infoTitle'],
          isWiderThanMd ? 'vc-text-H24' : 'vc-text-H20',
          'vc-text-bold',
        )}
      >
        {t('GET_PAID.TAX_INFORMATION_FORM.TAX_SUBTITLE', 'Tax Information')}
      </div>
      {/* Country */}
      <div
        className={clsx(
          'vc-formUnit',
          'vc-formUnit--select',
          styles['vc-formUnit'],
          styles['vc-formUnit--select'],
          {
            [styles.error + ' error']: errors?.country && touched?.country,
          },
          { [styles['vc-inputText--touched']]: dirty && touched?.country },
        )}
      >
        <Field
          as="select"
          name="country"
          aria-labelledby="label-country"
          className={clsx('vc-inputText', styles['vc-inputText'], {
            [styles['vc-inputText--touched']]: dirty && touched?.country,
          })}
          value={values.country}
          required
        >
          <option
            value=""
            disabled
          >
            {t('GET_PAID.PERSONAL_INFORMATION_FORM.PLACEHOLDER.COUNTRY')}
          </option>
          {countries.map((c) => (
            <option
              key={c.code}
              value={c.code}
            >
              {c.name}
            </option>
          ))}
        </Field>
        <label
          id="label-country"
          className={`vc-inputText-label ${styles['vc-inputText-label']} ${styles.required}`}
        >
          {t('GET_PAID.PERSONAL_INFORMATION_FORM.LABEL.COUNTRY')}
        </label>
        <ErrorMessage
          name="country"
          render={ErrorLabel}
        />
      </div>
      {/* Tax Number */}
      <div
        className={clsx('vc-formUnit', styles['vc-formUnit'], {
          [styles.error + ' error']: errors?.tin && touched?.tin,
        })}
      >
        <Field
          as="input"
          name="tin"
          aria-labelledby="label-tin"
          className={clsx('vc-inputText', styles['vc-inputText'], {
            [styles['vc-inputText--touched']]: dirty && touched?.tin,
          })}
          value={values.tin}
          maxLength={25}
          autoComplete="off"
          placeholder={t(taxInfo?.title)}
          required
        ></Field>
        <div className={clsx(styles.formGroup__labelContainer, 'vc-inputText-label', styles['vc-inputText-label'])}>
          <label
            id="label-tin"
            className={styles.formGroup__labelContainer__required}
          >
            {<span>{taxInfo?.title}</span>}
          </label>
          <TaxInfoModal taxInfo={taxInfo}>
            <Icon
              className={styles.infoBox}
              name="infobox-default"
              onClick={handleInfoIconClick}
              data-cy="tax_number_info_icon"
              ariaLabel={tA11y('A11Y.ICON.INFO')}
            />
          </TaxInfoModal>
        </div>
        <ErrorMessage
          name="tin"
          render={ErrorLabel}
        />
      </div>
      {userFlags?.isEUB2CUser && (
        <div
          className={clsx('vc-formUnit', styles['vc-formUnit'], {
            [styles.error + ' error']: errors?.vat && touched?.vat,
          })}
        >
          <SellerFormTextInput
            name="vat"
            label={t('GET_PAID.TAX_INFORMATION_FORM.VAT_FIELD', 'VAT Number')}
            value={values.vat}
            placeholder={t('GET_PAID.TAX_INFORMATION_FORM.VAT_FIELD', 'VAT Number')}
            required={false}
            maxLength={25}
            t={t}
            dirty={dirty}
            touched={touched}
          />
        </div>
      )}
    </div>
  );
};

export default TaxInformation;
